import React, { useEffect, useState } from "react";
import { X, Calendar, Package, AlertCircle } from "lucide-react";
import { useAuth } from "../../context/Auth";
import { useGlobalContext } from "../../context/stateContext";

const BatchChangeModal = ({
  show,
  onClose,
  currentItem,
  onSelectBatch,
  branch,
}) => {
  const [validBatches, setValidBatches] = useState([]);
  const { requestWithAuth } = useAuth();

  useEffect(() => {
    const fetchAvailableBatches = async () => {
      try {
        const response = await requestWithAuth(
          `/inventory/with-batches/${branch}?drug_id=${currentItem.drug}&paginated=false`
        );

        const availableBatches = response[0]?.batches
          ?.filter(
            (batch) =>
              batch.expiry_date &&
              !isBatchExpired(batch.expiry_date) &&
              parseFloat(batch.quantity) > 0
          )
          ?.sort((a, b) => new Date(a.expiry_date) - new Date(b.expiry_date));


        setValidBatches(availableBatches || []);
      } catch (error) {
        console.error("Error fetching available batches:", error);
      }
    };

    if (currentItem?.drug) fetchAvailableBatches();
  }, [currentItem.drug, branch]);

  const isBatchExpired = (expiryDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);
    return expiry < today;
  };

  const isExpiringSoon = (expiryDate) => {
    const threeMonthsFromNow = new Date();
    threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
    return new Date(expiryDate) <= threeMonthsFromNow;
  };

  // Filter out expired batches and sort by expiry date

  if (!show || !currentItem) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Package size={24} className="text-blue-500" />
              Change Batch
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Select a different batch for {currentItem.drug_name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Current Batch Info */}
        <div className="p-6 bg-blue-50 border-b border-blue-100">
          <p className="text-sm font-medium text-gray-700 mb-2">
            Current Batch
          </p>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-gray-900">
                {currentItem.batch_number}
              </p>
              <p className="text-sm text-gray-600">
                Quantity: {currentItem.quantity} | Available:{" "}
                {currentItem.available_quantity}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Expires</p>
              <p className="font-medium text-gray-900">
                {new Date(currentItem.expiry_date).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Available Batches */}
        <div className="p-6 max-h-96 overflow-y-auto">
          <p className="text-sm font-medium text-gray-700 mb-4">
            Available Batches ({validBatches?.length})
          </p>

          {validBatches?.length === 0 ? (
            <div className="text-center py-8">
              <Package size={48} className="mx-auto text-gray-300 mb-3" />
              <p className="text-sm text-gray-500">
                No other batches available
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {validBatches?.map((batch, index) => {
                const isCurrentBatch =
                  batch.batch_number === currentItem.batch_number;
                const expiringSoon = isExpiringSoon(batch.expiry_date);

                return (
                  <div
                    key={index}
                    onClick={() => !isCurrentBatch && onSelectBatch(batch)}
                    className={`
                      p-4 rounded-lg border-2 transition-all cursor-pointer
                      ${isCurrentBatch
                        ? "border-blue-500 bg-blue-50 cursor-not-allowed"
                        : "border-gray-200 hover:border-blue-400 hover:bg-blue-50"
                      }
                      ${expiringSoon && !isCurrentBatch
                        ? "border-orange-200"
                        : ""
                      }
                    `}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <p className="font-semibold text-gray-900">
                            {batch.batch_number}
                          </p>
                          {isCurrentBatch && (
                            <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded-full">
                              Current
                            </span>
                          )}
                          {expiringSoon && !isCurrentBatch && (
                            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full flex items-center gap-1">
                              <AlertCircle size={12} />
                              Expiring Soon
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-gray-600">Available Stock</p>
                            <p className="font-medium text-gray-900">
                              {batch.quantity} units
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600 flex items-center gap-1">
                              <Calendar size={14} />
                              Expiry Date
                            </p>
                            <p className="font-medium text-gray-900">
                              {new Date(batch.expiry_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>

                        {expiringSoon && !isCurrentBatch && (
                          <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded">
                            <p className="text-xs text-orange-700">
                              ⚠️ This batch expires within 3 months
                            </p>
                          </div>
                        )}
                      </div>

                      {!isCurrentBatch && (
                        <div className="ml-4">
                          <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center">
                            <div className="w-3 h-3 rounded-full bg-transparent"></div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Click on a batch to select it
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchChangeModal;
